import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, ArrowRight, MessageSquare, Mail, Phone, ShoppingBag, AlertTriangle,
  QrCode, Voicemail, Loader2, Clock, Sparkles,
} from 'lucide-react';
import ScamIQBadge from '../components/ScamIQBadge';
import PremiumScenarioCard from '../components/PremiumScenarioCard';
import { API_URL } from '../config';
import '../styles/ScamSmart.css';

const API = `${API_URL}/api`;
const SHIELD_LOGO = 'https://customer-assets.emergentagent.com/job_express-checkout-9/artifacts/gw8x19qe_Variant%201_Shield%20Only_Vector%20copy.png';

const CHANNEL_ICONS = {
  sms: MessageSquare, email: Mail, call: Phone, voicemail: Voicemail, dm: MessageSquare,
  marketplace: ShoppingBag, whatsapp: MessageSquare, website: AlertTriangle, qr: QrCode, invoice: Mail,
};
const CHANNEL_LABELS = {
  sms: 'SMS', email: 'Email', call: 'Phone call', voicemail: 'Voicemail', dm: 'Social DM',
  marketplace: 'Marketplace', whatsapp: 'WhatsApp', website: 'Website', qr: 'QR code', invoice: 'Invoice',
};

const CATEGORY_LABEL = {
  all: 'All categories',
  family_emergency: 'Family emergency',
  banking_government: 'Banking & gov',
  delivery_shopping: 'Delivery & shopping',
  marketplace_payid: 'Marketplace / PayID',
  account_security: 'Account security',
  emerging: 'Emerging',
};
const DIFFICULTIES = ['all', 'easy', 'medium', 'hard'];

const ScamSmartLibrary = ({ ssEmail }) => {
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState('all');
  const [diff, setDiff] = useState('all');
  const [openScenario, setOpenScenario] = useState(null);
  const [practiceIds, setPracticeIds] = useState([]);

  useEffect(() => {
    axios.get(`${API}/scamsmart/v2/scenarios/all`, { params: { email: ssEmail } })
      .then(({ data }) => setScenarios(data.scenarios || []))
      .catch(() => {})
      .finally(() => setLoading(false));
    axios.get(`${API}/scamsmart/practice/config`).then(({ data }) => setPracticeIds(data.available_scenarios || [])).catch(() => {});
  }, [ssEmail]);

  const filtered = useMemo(() => {
    return scenarios.filter((s) => (cat === 'all' || s.category === cat) && (diff === 'all' || s.difficulty === diff));
  }, [scenarios, cat, diff]);

  const openScenarioModal = (s) => {
    setOpenScenario(s);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="ss-page" data-testid="ss-library-page">
      <header className="ss-header">
        <div className="ss-header-inner">
          <Link to="/scamsmart/dashboard" className="ss-brand">
            <img src={SHIELD_LOGO} alt="" className="ss-brand-mark" />
            <div>
              <div className="ss-brand-text">All Secured</div>
              <div className="ss-brand-sub">Scenario Library</div>
            </div>
          </Link>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <ScamIQBadge />
            <Link to="/scamsmart/dashboard" className="ss-header-cta">Dashboard</Link>
          </div>
        </div>
      </header>

      <div className="ss-test-wrap">
        <div className="ss-test-container" style={{ maxWidth: 1100 }}>
          <Link to="/scamsmart/dashboard" className="ss-back"><ArrowLeft size={14} /> Dashboard</Link>

          {!openScenario && (
            <>
              <div className="ss-section-eyebrow">Scenario Library</div>
              <h1 className="ss-section-title" style={{ fontSize: 28 }}>Premium training scenarios</h1>
              <p className="ss-section-lede" style={{ fontSize: 15 }}>
                Each scenario is a 90-second immersive moment — with the real message surface, optional
                clues to investigate, and three-panel consequence feedback. Filter by category to train a
                specific weak spot, or by difficulty when you want a quick easy one.
              </p>

              <div className="ss-lib-filters" role="toolbar" data-testid="ss-lib-cat-filters">
                {Object.entries(CATEGORY_LABEL).map(([k, label]) => (
                  <button key={k} className={`ss-lib-filter ${cat === k ? 'active' : ''}`} onClick={() => setCat(k)} data-testid={`ss-lib-cat-${k}`}>
                    {label}
                  </button>
                ))}
              </div>
              <div className="ss-lib-filters" data-testid="ss-lib-diff-filters">
                {DIFFICULTIES.map((d) => (
                  <button key={d} className={`ss-lib-filter ${diff === d ? 'active' : ''}`} onClick={() => setDiff(d)}>
                    {d === 'all' ? 'All difficulties' : d}
                  </button>
                ))}
              </div>

              {loading && (
                <div className="ss-empty-state">
                  <Loader2 size={28} style={{ animation: 'ss-spin 1s linear infinite', color: '#16B1C4' }} />
                </div>
              )}
              {!loading && filtered.length === 0 && (
                <div className="ss-empty-state">No scenarios match these filters yet.</div>
              )}
              {!loading && filtered.length > 0 && (
                <div className="ss-lib-grid" data-testid="ss-lib-grid">
                  {filtered.map((s) => {
                    const Icon = CHANNEL_ICONS[s.messageType] || MessageSquare;
                    const isMulti = s.scenarioType === 'multi_step';
                    const summary = s.scamPatternSummary || s.scenarioIntro?.scene || '';
                    return (
                      <button key={s.id} className="ss-lib-card" onClick={() => openScenarioModal(s)} data-testid={`ss-lib-card-${s.id}`}>
                        <div className="ss-scenario-meta" style={{ marginBottom: 0 }}>
                          <span className="ss-tag ss-tag-channel">
                            <Icon size={11} style={{ verticalAlign: '-2px', marginRight: 4 }} /> {CHANNEL_LABELS[s.messageType] || 'Message'}
                          </span>
                          <span className="ss-tag">{s.difficulty}</span>
                          {isMulti && <span className="ss-tag" style={{ background: 'rgba(242, 178, 51, 0.1)', color: '#C9842A' }}>Multi-step</span>}
                        </div>
                        <h3 className="ss-lib-card-title">{s.title}</h3>
                        <p className="ss-lib-card-snippet">{summary}</p>
                        <div className="ss-lib-card-meta">
                          <Clock size={11} style={{ verticalAlign: '-1px', marginRight: 4 }} />
                          ~{s.estimatedSeconds || 90}s · reviewed {s.source?.lastReviewedDate}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
              <p className="ss-disclaimer">
                ScamSmart scenarios are built around common scam patterns and public guidance from trusted Australian sources.
              </p>
            </>
          )}

          {openScenario && (
            <div data-testid="ss-lib-scenario">
              <button className="ss-back" onClick={() => setOpenScenario(null)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}>
                <ArrowLeft size={14} /> Back to library
              </button>
              {practiceIds.includes(openScenario.id) && (
                <div className="ss-lib-practice-cta" data-testid="ss-lib-practice-cta">
                  <div>
                    <span className="ss-lib-practice-tag"><Sparkles size={11} /> AI Practice Mode</span>
                    <p>Want to practise this scenario live against the AI scammer? You'll chat back and forth, then get scored on how well you defended yourself.</p>
                  </div>
                  <Link
                    to={`/scamsmart/practice/${openScenario.id}`}
                    className="ss-btn-primary"
                    data-testid={`ss-lib-practice-${openScenario.id}`}
                  >
                    Practise live <ArrowRight size={14} />
                  </Link>
                </div>
              )}
              <PremiumScenarioCard
                key={openScenario.id}
                scenario={openScenario}
                onComplete={() => { /* stay on this scenario; user can navigate back */ }}
              />
              <div className="ss-next-row" style={{ padding: '0 0 20px', justifyContent: 'flex-start' }}>
                <button className="ss-btn-ghost" onClick={() => setOpenScenario(null)}>
                  <ArrowLeft size={14} /> Back to library
                </button>
                <Link to="/scamsmart/library" className="ss-btn-primary" onClick={() => setOpenScenario(null)}>
                  Train another scenario <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          )}
          <style>{`@keyframes ss-spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
        </div>
      </div>
    </div>
  );
};

export default ScamSmartLibrary;
